"use client";

import ChevronRightIcon from "@/components/core/icons/ChevronRightIcon";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";

// skipTrailingSlashRedirect is enabled, so "/guides/foo/" is a reachable URL
// and a plain pathname.split("/").pop() would return "" for it instead of
// the slug. Drop empty segments (from a leading or trailing slash) first.
export function getLastPathSegment(pathname: string): string {
  const segments = pathname.split("/").filter((segment) => segment !== "");
  return segments[segments.length - 1] ?? "";
}

export default function PageSideNavigationLink({
  nav,
  index,
  initialOpen,
  isMobile,
  basePath,
  isOpen,
  onToggle,
  activeCategory,
}: {
  nav: {
    title: string;
    slug: string;
    children?: {
      title: string;
      slug: string;
    }[];
  };
  index: number;
  initialOpen?: boolean;
  isMobile?: boolean;
  basePath: string;
  isOpen?: boolean;
  onToggle?: () => void;
  activeCategory?: string | string[];
}) {
  const pathname = usePathname();
  
  const calculateActiveChildIndex = () => {
    if (!nav.children) return -1;
    const childPath = getLastPathSegment(pathname);
    return nav.children.findIndex((child) =>
      child ? childPath === child.slug : false
    );
  };

  const initialActiveChildIndex = calculateActiveChildIndex();
  const initialActiveChildTop = -1;

  const [internalIsOpen, setInternalOpen] = useState(initialOpen);
  const [activeChildIndex, setActiveChildIndex] = useState(initialActiveChildIndex);
  const [activeChildTop, setActiveChildTop] = useState(initialActiveChildTop);
  const [contentHeight, setContentHeight] = useState(0);
  const childRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Use external isOpen if provided, otherwise use internal state
  const currentIsOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  
  // Determine if this section is active based on current page
  const activeCategoryString = Array.isArray(activeCategory) ? activeCategory[0] : activeCategory;
  const currentSlug = getLastPathSegment(pathname);
  const isActive = nav.slug === activeCategoryString ||
                   currentSlug === nav.slug ||
                   (nav.children ? nav.children.some(child => child.slug === currentSlug) : false);

  useEffect(() => {
    if (isMobile) setInternalOpen(true);
  }, [isMobile]);

  useEffect(() => {
    // Update active child index when pathname changes
    const childPath = getLastPathSegment(pathname);
    const newIndex = nav.children
      ? nav.children.findIndex((child) => (child ? childPath === child.slug : false))
      : -1;
    setActiveChildIndex(newIndex);

    // Calculate position immediately if DOM is available
    if (newIndex >= 0 && childRefs.current[newIndex] && currentIsOpen) {
      const activeElement = childRefs.current[newIndex]?.firstChild as HTMLAnchorElement;
      if (activeElement) {
        const container = childRefs.current[newIndex]?.parentElement;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = activeElement.getBoundingClientRect();
          const elementTop = elementRect.top - containerRect.top - 8;
          const elementHeight = elementRect.height;
          const redLineHeight = 16; // h-4 = 16px
          const centeredTop = elementTop + (elementHeight / 2) - (redLineHeight / 2);
          setActiveChildTop(centeredTop);
        }
      }
    }
  }, [pathname, nav.children, currentIsOpen]);

  useEffect(() => {
    if (activeChildIndex >= 0 && childRefs.current[activeChildIndex] && currentIsOpen) {
      requestAnimationFrame(() => {
        const activeElement = childRefs.current[activeChildIndex]?.firstChild as HTMLAnchorElement;
        if (activeElement) {
          const container = childRefs.current[activeChildIndex]?.parentElement;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = activeElement.getBoundingClientRect();
            const elementTop = elementRect.top - containerRect.top - 8;
            const elementHeight = elementRect.height;
            const redLineHeight = 16;
            const centeredTop = elementTop + (elementHeight / 2) - (redLineHeight / 2);

            setActiveChildTop(prev => Math.abs(centeredTop - prev) > 2 ? centeredTop : prev);
          }
        }
      });
    }
  }, [activeChildIndex, currentIsOpen]);

  useEffect(() => {
    setInternalOpen(initialOpen);
  }, [initialOpen]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    // A hidden ancestor (e.g. the collapsed mobile drawer) collapses this
    // element to 0x0. nav.children is non-empty here, so a real measurement
    // is never 0. Ignore 0 readings and keep the last known height instead
    // of flashing the section shut while it is hidden. ResizeObserver re-fires
    // with the true size once the ancestor becomes visible again.
    const updateHeight = () => {
      const height = el.scrollHeight;
      if (height > 0) setContentHeight(height);
    };
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [nav.children]);

  if (!pathname) return null;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen(!internalIsOpen);
    }
  };

  return (
    <div className="flex flex-col" data-active={currentIsOpen}>
      <div className="flex items-center gap-2 font-medium text-sm leading-none py-1 self-start">
        <div
          className="w-3.5 aspect-square relative cursor-pointer"
          onClick={handleToggle}
        >
          {nav.children && nav.children.length > 0 && (
            <div
              className={clsx(
                "aspect-square absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 transition-all duration-500 ease-in-out",
                (currentIsOpen ? "rotate-90" : "rotate-0 text-grey-800")
              )}
            >
              <ChevronRightIcon />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className={clsx([
            "text-neutral-400 hover:text-white font-medium rounded py-2 transition-all duration-150 ease-in-out leading-none text-left cursor-pointer",
            isActive ? "bg-grey-850 p-2 text-white" : "bg-transparent",
          ])}
        >
          {nav.title}
        </button>
      </div>
      {nav.children && nav.children.length > 0 && (
        <div
          className={clsx(
            "transition-all duration-500 ease-in-out overflow-hidden"
          )}
          style={{
            maxHeight: currentIsOpen ? `${contentHeight}px` : "0px",
            paddingBottom: currentIsOpen ? "8px" : "0px",
          }}
        >
          <div
            ref={contentRef}
            className={clsx(
              "pl-6 relative"
            )}
          >
            <div className="absolute w-0.5 bg-grey-850 left-1.5 top-2 bottom-2">
              <div
                className={clsx(
                  "absolute z-10 left-0 right-0 bg-red-500 h-4",
                  (activeChildIndex >= 0 ? "transition-all" : "opacity-0")
                )}
                style={{
                  backgroundColor: "#ef4444",
                  width: "2px",
                  top: activeChildTop >= 0 ? activeChildTop + "px" : "0",
                }}
              />
            </div>
            {nav.children.map((child, childIndex) => {
              if (!child || !child.slug) return null;
              const href = "/"+ basePath +"/" + child.slug;
              return (
                <Link
                  ref={(el) => { childRefs.current[childIndex] = el; }}
                  href={href}
                  key={index + "-" + childIndex}
                  className="flex items-center gap-2 min-h-8 font-medium text-sm py-2 pl-2 md:pl-0 self-start"
                >
                  <span className={clsx("font-medium text-neutral-400 hover:text-white", {
                    "text-white": child.slug === currentSlug,
                  })}>
                    {child.title}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
